import { OnboardingConfig } from 'config/onboarding'
import {
	AppButton,
	Modal,
	ModalHeader,
	useEnterExit
} from '@clickguard/design-system'

/**
 * What the backend says about the ad account's spend across the three recent 30-day windows.
 * The thresholds live on the backend and no amount reaches the client, so
 * the page can never state or leak one.
 * - `low`: under the first bar, no spend at all included. Not allowed on;
 *   connect a more active account.
 * - `screening`: enough to talk. Requires a fit review and an administrator override.
 * - `vip`: a big account. Goes on; the VIP setup offer comes after the
 *   account connects (the onboarding interstitial), not here.
 */
export type ScreeningTier = 'low' | 'screening' | 'vip'

interface SpendScreeningModalProps {
	/** `null` (or `vip`) keeps the modal closed. */
	tier: ScreeningTier | null
	onChooseAnotherAccount: () => void
	/** Opens Settings > Billing, where the subscription is managed. */
	onManageSubscription: () => void
}

const PANEL_MS = 180

/**
 * Spend-based screening, raised over the ad account picker when the picked
 * account is below the bar (design kickoff, 5 Oct 2026; modal format per
 * the 7 Oct review: "something happened, so I did not move forward"). Same
 * frame as the zero-spend modal. Panels swap with a short fade (DS
 * `useEnterExit`).
 */
export const SpendScreeningModal = ({
	tier,
	onChooseAnotherAccount,
	onManageSubscription
}: SpendScreeningModalProps) => {
	const open = tier === 'low' || tier === 'screening'
	const panel = tier === 'low' ? 'low' : 'offer'
	const { mounted, entered } = useEnterExit(open, { duration: PANEL_MS })

	const openBooking = () => {
		window.open(
			OnboardingConfig.qualificationCallUrl,
			'_blank',
			'noopener,noreferrer'
		)
	}

	// Secondary where it is the only other way out, tertiary where a second
	// option already sits above it.
	const manageSubscription = (level: 'secondary' | 'tertiary') => (
		<AppButton
			variant={level === 'secondary' ? 'secondary' : 'ghost'}
			size={level === 'secondary' ? 'lg' : 'md'}
			fullWidth
			label="Manage subscription"
			onClick={onManageSubscription}
		/>
	)

	return (
		<Modal
			open={open}
			onClose={onChooseAnotherAccount}
			closeOnOverlayClick={false}
			closeOnEscape={false}
			size="md"
			rounded="modal"
			// Force above the post-onboarding add-account GDialog (z-[10000001]).
			overlayClassName="!z-[10000002]"
			className="px-12 py-8"
		>
			<div
				className={`flex flex-col gap-6 transition-[opacity,transform] duration-[180ms] ease-out motion-reduce:transition-none ${
					mounted && entered
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-1'
				}`}
			>
				{panel === 'low' && (
					<>
						<Header
							title="Not enough activity detected"
							subtitle="This account doesn’t have enough activity. ClickGuard needs enough recent historical data to learn your traffic patterns and accurately block live fraud. Please connect or switch to a more active account to proceed."
						/>
						<div className="flex flex-col gap-3">
							<AppButton
								variant="primary"
								size="lg"
								fullWidth
								label="Select a different account"
								onClick={onChooseAnotherAccount}
							/>
							{manageSubscription('secondary')}
						</div>
					</>
				)}

				{panel === 'offer' && (
					<>
						<Header
							title="Let’s make sure we’re a good fit"
							subtitle="This account has some characteristics that require verification. Please book a quick 15-minute call with a specialist so we can confirm whether ClickGuard is the right solution before you get started."
						/>
						<div className="flex flex-col gap-3">
							<AppButton
								variant="primary"
								size="lg"
								fullWidth
								label="Book a 15-minute call"
								onClick={openBooking}
							/>
							<AppButton
								variant="secondary"
								size="lg"
								fullWidth
								label="Select a different account"
								onClick={onChooseAnotherAccount}
							/>
							{manageSubscription('tertiary')}
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}

const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
	<ModalHeader
		size="lg"
		titleColor="navy"
		// ModalHeader has no 24px size (sm 20 / md 30 / lg 36); pin the title to the Figma 24/30.
		className="[&_h2]:!text-[24px] [&_h2]:!leading-[30px] [&_h2]:!tracking-normal"
		title={title}
		subtitle={subtitle}
	/>
)
